/**
 * SearchInputHelperService
 *
 * Purpose: Extract low-risk utility functions for search input management
 * Phase: 2a (Week 2)
 * Risk: LOW - Pure functions, easy to test
 * Lines Extracted: Various helper methods from nav-bar.component.ts
 *
 * Responsibilities:
 * - URL validation
 * - Text length validation
 * - Placeholder management
 * - Cursor position utilities
 * - File name truncation
 * - Content editable management
 *
 * NOT Responsible For:
 * - Event handling (moved to SearchInputHandlerService in Phase 2b)
 * - HTTP requests
 * - Signal updates
 */

import {ElementRef, Injectable} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';

@Injectable()
export class SearchInputHelperService {
  constructor(private sanitizer: DomSanitizer) {
  }

  /**
   * Check if input is a URL
   * Extracted from lines 2178-2198
   *
   * @param input - Text to check
   * @returns true if input is a URL
   */
  isUrl(input: string): boolean {
    if (input.includes('https')) {
      return true;
    }

    try {
      new URL(input);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Validate input length
   *
   * @param input - Text to validate
   * @param maxLength - Maximum allowed length
   * @returns true if within limits
   */
  validateLength(input: string, maxLength: number): boolean {
    return input.length <= maxLength;
  }

  /**
   * Truncate text to max length and ensure cursor stays at end
   *
   * @param input - Text to truncate
   * @param maxLength - Maximum length
   * @param editableDiv - Div element to update
   * @returns Truncated text
   */
  truncateAndUpdate(input: string, maxLength: number, editableDiv: HTMLElement): string {
    if (input.length > maxLength) {
      const truncated = input.substring(0, maxLength);
      editableDiv.innerText = truncated;
      this.moveCursorToEnd(editableDiv);
      return truncated;
    }
    return input;
  }

  /**
   * Add placeholder text to editable div
   * Extracted from lines 780-787
   *
   * @param editableDiv - The contenteditable div
   * @param placeholder - Placeholder text to show
   */
  addPlaceholder(editableDiv: ElementRef<HTMLDivElement>, placeholder: string): void {
    const div = editableDiv.nativeElement;
    if (!div.innerText.trim()) {
      div.classList.add('placeholder');
      div.innerText = placeholder;
      div.blur();
    }
  }

  /**
   * Remove placeholder text from editable div
   * Extracted from lines 789-795
   *
   * @param editableDiv - The contenteditable div
   * @param placeholder - The placeholder text to check against (optional)
   */
  removePlaceholder(editableDiv: ElementRef<HTMLDivElement>, placeholder?: string): void {
    const div = editableDiv.nativeElement;
    if (div.classList.contains('placeholder')) {
      div.classList.remove('placeholder');
      // Only clear the content if it's actually the placeholder text or empty
      // This prevents clearing existing query content when clicking to edit
      if (!placeholder || div.innerText.trim() === placeholder || div.innerText.trim() === '') {
        div.innerText = '';
      }
    }
  }

  /**
   * Check if div has placeholder
   *
   * @param editableDiv - The contenteditable div
   * @returns true if placeholder is present
   */
  hasPlaceholder(editableDiv: ElementRef<HTMLDivElement>): boolean {
    return editableDiv.nativeElement.classList.contains('placeholder');
  }

  /**
   * Move cursor to end of div
   * Extracted from lines 1747-1757
   *
   * @param div - The div element
   */
  moveCursorToEnd(div: HTMLElement): void {
    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(div);
    range.collapse(false);
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  /**
   * Get current cursor position in div
   *
   * @param div - The div element
   * @returns Cursor position as offset
   */
  getCursorPosition(div: HTMLElement): number {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return 0;

    const range = sel.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(div);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    return preCaretRange.toString().length;
  }

  /**
   * Set cursor position in div
   *
   * @param div - The div element
   * @param position - Character offset
   */
  setCursorPosition(div: HTMLElement, position: number): void {
    const range = document.createRange();
    const sel = window.getSelection();

    if (!sel) return;

    const textNode = this.getTextNodeAtPosition(div, position);
    if (textNode) {
      range.setStart(textNode.node, textNode.offset);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  /**
   * Truncate file name to max length
   * Extracted from lines 1333-1343
   *
   * @param fileName - File name to truncate
   * @param maxLength - Maximum length (default 15)
   * @returns Truncated file name
   */
  truncateFileName(fileName: string, maxLength: number = 15): string {
    if (!fileName) {
      return '';
    }
    if (fileName.length <= maxLength) {
      return fileName;
    }
    return fileName.substring(0, maxLength) + '...';
  }

  /**
   * Sanitize input text (strip HTML, trim whitespace)
   *
   * @param text - Text to sanitize
   * @returns Sanitized text
   */
  sanitizeInput(text: string): string {
    // Create a temporary div to strip HTML
    const temp = document.createElement('div');
    temp.innerHTML = text;
    return (temp.textContent || temp.innerText || '').trim();
  }

  /**
   * Set contenteditable attribute on element
   * Extracted from lines 1345-1349
   *
   * @param element - Element to modify
   * @param isEditable - Whether element should be editable
   */
  setContentEditable(element: ElementRef<HTMLElement> | HTMLElement, isEditable: boolean): void {
    const el = element instanceof ElementRef ? element.nativeElement : element;
    el.setAttribute('contenteditable', isEditable.toString());
  }

  /**
   * Check if text contains only whitespace
   *
   * @param text - Text to check
   * @returns true if empty or whitespace
   */
  isEmpty(text: string): boolean {
    return !text || text.trim() === '';
  }

  /**
   * Extract plain text from SafeHtml
   *
   * @param html - SafeHtml to convert
   * @returns Plain text content
   */
  htmlToPlainText(html: SafeHtml): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html.toString(), 'text/html');

    if (doc.body.textContent) {
      return doc.body.textContent.split(': ')[1]?.trim()?.split('(see ')[0]?.trim() || '';
    }

    return '';
  }

  /**
   * Check if input matches forbidden words (used for prompter activation)
   * Extracted from lines 2200-2211
   *
   * @param input - User input to check
   * @returns true if contains forbidden words
   */
  containsForbiddenWords(input: string): boolean {
    const words = input.trim().split(/\s+/);
    const forbiddenWords = ['like', 'song', 'similar'];

    return !words.some((word) => forbiddenWords.includes(word.toLowerCase()));
  }

  /**
   * Find duplicate words in text (used to skip real animation)
   * Extracted from lines 2124-2145
   *
   * @param text - Text to check
   * @returns true if duplicates found
   */
  findDuplicateWord(text: string): boolean {
    const words = text.toLowerCase().match(/\b(\w+)\b/g) || [];
    const exclusions = new Set(['or', 'in', 'that', 'this', 'is', 'are', 'and', 'a', 'not', 'by']);
    const seenWords = new Set<string>();

    for (const word of words) {
      if (exclusions.has(word)) {
        continue;
      }
      if (seenWords.has(word)) {
        return true;
      }
      seenWords.add(word);
    }

    return false;
  }

  /**
   * Insert text at cursor position
   *
   * @param div - The div element
   * @param text - Text to insert
   */
  insertTextAtCursor(div: HTMLElement, text: string): void {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // Fallback: replace entire content
      div.textContent = text;
    }
  }

  /**
   * Get word count in text
   *
   * @param text - Text to count
   * @returns Number of words
   */
  getWordCount(text: string): number {
    return text.trim().split(/\s+/).filter((word) => word.length > 0).length;
  }

  /**
   * Check if text should trigger prompter mode
   * Based on word count and forbidden word detection
   *
   * @param text - Text to check
   * @returns true if should activate prompter
   */
  shouldActivatePrompter(text: string): boolean {
    const wordCount = this.getWordCount(text);
    return wordCount > 0 && this.containsForbiddenWords(text);
  }

  /**
   * Escape HTML special characters
   *
   * @param text - Text to escape
   * @returns Escaped text
   */
  escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Unescape HTML entities
   *
   * @param html - HTML to unescape
   * @returns Unescaped text
   */
  unescapeHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  /**
   * Helper: Find text node at specific position
   * Used by setCursorPosition
   *
   * @private
   */
  private getTextNodeAtPosition(
    root: Node,
    position: number
  ): { node: Node; offset: number } | null {
    let currentPosition = 0;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

    let node: Node | null = walker.nextNode();
    while (node) {
      const nodeLength = node.textContent?.length || 0;
      if (currentPosition + nodeLength >= position) {
        return {
          node,
          offset: position - currentPosition
        };
      }
      currentPosition += nodeLength;
      node = walker.nextNode();
    }

    return null;
  }
}
